import { getReferenceString, MedplumClient, MedplumRequestOptions } from '@medplum/core';
import {
  Claim,
  ClaimResponse,
  Coverage,
  DocumentReference,
  Organization,
  Patient,
  Reference,
} from '@medplum/fhirtypes';
import {
  BENEFIT_APPROVAL_DEFAULT_VALID_DAYS,
  BENEFIT_DECISION_CODESYSTEM,
  BENEFIT_DENIAL_REASON_CODESYSTEM,
  BenefitDecision,
  BenefitDenialReason,
  COVERAGE_ACTION_CODESYSTEM,
  CoverageAction,
} from './constants';
import { resolveActiveEpisodeOfCare } from './episode-of-care';
import {
  BENEFIT_DECISION_EXTENSION,
  BENEFIT_DENIAL_REASON_EXTENSION,
  COVERAGE_ACTION_DOCUMENT_EXTENSION,
  COVERAGE_ACTION_EXTENSION,
  COVERAGE_EPISODE_OF_CARE_EXTENSION,
} from './extensions';
import { readCoverageAction } from './payment-status';

/**
 * Employer benefit events (BAR-2593): Submitted is a pre-authorization `Claim`, Approved /
 * Denied a `ClaimResponse`. Append-only — a correction is a new resource, and the current
 * decision is the most recently RECORDED one on the active episode's Coverage. The panel
 * (BAR-2594), the stage bot (BAR-2595) and Won (BAR-2645) read it through
 * `getCurrentBenefitDecision` so there is one rule, not three.
 */

const CLAIM_TYPE_PROFESSIONAL = {
  coding: [{ system: 'http://terminology.hl7.org/CodeSystem/claim-type', code: 'professional' }],
};
const PRIORITY_NORMAL = {
  coding: [{ system: 'http://terminology.hl7.org/CodeSystem/processpriority', code: 'normal' }],
};
const INFO_CATEGORY = {
  coding: [{ system: 'http://terminology.hl7.org/CodeSystem/claiminformationcategory', code: 'info' }],
};

export interface BenefitCoverage {
  coverage: Coverage;
  payor: Reference<Organization>;
  payorName: string;
}

/** The active Coverage belonging to the patient's active episode, if any. */
export async function resolveActiveCoverage(medplum: MedplumClient, patientId: string): Promise<Coverage | undefined> {
  const episode = await resolveActiveEpisodeOfCare(medplum, patientId);
  if (!episode) {
    return undefined;
  }
  const episodeReference = getReferenceString(episode);
  const coverages = await medplum.searchResources('Coverage', {
    beneficiary: `Patient/${patientId}`,
    status: 'active',
  });
  // Coverage has no episode search parameter, so match in code — as bariendo-be does.
  return coverages.find((coverage) =>
    coverage.extension?.some(
      (extension) =>
        extension.url === COVERAGE_EPISODE_OF_CARE_EXTENSION && extension.valueReference?.reference === episodeReference
    )
  );
}

/**
 * The Coverage plus its payor, when the payor is an Organization. An employer benefit with no
 * administrator has `payor → Patient` by design; there is nobody to submit to, so undefined.
 */
export async function resolveBenefitCoverage(
  medplum: MedplumClient,
  patientId: string
): Promise<BenefitCoverage | undefined> {
  const coverage = await resolveActiveCoverage(medplum, patientId);
  const payorReference = coverage?.payor?.[0]?.reference;
  if (!coverage || !payorReference?.startsWith('Organization/')) {
    return undefined;
  }
  const payor = await medplum.readReference<Organization>({ reference: payorReference });
  return {
    coverage,
    payor: { reference: payorReference, display: payor.name },
    payorName: payor.name ?? 'the payor',
  };
}

export interface BenefitSubmittedInput {
  patient: Reference<Patient>;
  benefitCoverage: BenefitCoverage;
  provider: Reference<Organization>;
  /** YYYY-MM-DD, operator-entered. */
  submittedOn: string;
  note?: string;
}

export function buildBenefitSubmittedClaim(input: BenefitSubmittedInput): Claim {
  const { patient, benefitCoverage, provider, submittedOn, note } = input;
  return {
    resourceType: 'Claim',
    status: 'active',
    use: 'preauthorization',
    type: CLAIM_TYPE_PROFESSIONAL,
    priority: PRIORITY_NORMAL,
    patient,
    created: submittedOn,
    provider,
    insurer: benefitCoverage.payor,
    insurance: [{ sequence: 1, focal: true, coverage: { reference: getReferenceString(benefitCoverage.coverage) } }],
    // R4 Claim has no note element; supportingInfo `info` is the native place for free text.
    ...(note ? { supportingInfo: [{ sequence: 1, category: INFO_CATEGORY, valueString: note }] } : {}),
  };
}

export interface BenefitDecisionInput {
  patient: Reference<Patient>;
  benefitCoverage: BenefitCoverage;
  decision: BenefitDecision;
  /** YYYY-MM-DD, operator-entered — the date commission reads. */
  decidedOn: string;
  /** YYYY-MM-DD. Required for approvals, never before `decidedOn`. */
  validThrough?: string;
  /** Required for denials. */
  denialReason?: BenefitDenialReason;
  note?: string;
  /** The Submitted Claim, when one was recorded. */
  request?: Reference<Claim>;
}

export function buildBenefitDecisionResponse(input: BenefitDecisionInput): ClaimResponse {
  const { patient, benefitCoverage, decision, decidedOn, validThrough, denialReason, note, request } = input;

  if (decision === BenefitDecision.APPROVED && (!validThrough || validThrough < decidedOn)) {
    throw new Error('An approval needs a valid-through date on or after the decision date');
  }
  if (decision === BenefitDecision.DENIED && !denialReason) {
    throw new Error('A denial needs a reason');
  }

  return {
    resourceType: 'ClaimResponse',
    status: 'active',
    use: 'preauthorization',
    type: CLAIM_TYPE_PROFESSIONAL,
    patient,
    created: decidedOn,
    insurer: benefitCoverage.payor,
    // `complete` for both: outcome is processing status, not the decision.
    outcome: 'complete',
    insurance: [{ sequence: 1, focal: true, coverage: { reference: getReferenceString(benefitCoverage.coverage) } }],
    ...(request ? { request } : {}),
    ...(decision === BenefitDecision.APPROVED ? { preAuthPeriod: { start: decidedOn, end: validThrough } } : {}),
    ...(note ? { processNote: [{ text: note }] } : {}),
    extension: [
      { url: BENEFIT_DECISION_EXTENSION, valueCoding: { system: BENEFIT_DECISION_CODESYSTEM, code: decision } },
      ...(denialReason && decision === BenefitDecision.DENIED
        ? [
            {
              url: BENEFIT_DENIAL_REASON_EXTENSION,
              valueCoding: { system: BENEFIT_DENIAL_REASON_CODESYSTEM, code: denialReason },
            },
          ]
        : []),
    ],
  };
}

/** The steps we send; every other action is the payor's answer (BAR-3076). */
const REQUEST_ACTIONS: ReadonlySet<CoverageAction> = new Set([
  CoverageAction.REFERRAL_SENT,
  CoverageAction.CASE_FORM_SUBMITTED,
  CoverageAction.BENEFIT_VERIFIED,
  CoverageAction.QUESTIONNAIRE_RECEIVED,
  CoverageAction.PSYCH_EVALUATION_RECEIVED,
  CoverageAction.PCP_CLEARANCE_RECEIVED,
  CoverageAction.PRIOR_AUTH_SUBMITTED,
]);

/** A step we send (a Claim), not the payor's answer. */
export function isCoverageRequest(action: CoverageAction): boolean {
  return REQUEST_ACTIONS.has(action);
}

export interface CoverageActionInput {
  patient: Reference<Patient>;
  benefitCoverage: BenefitCoverage;
  provider: Reference<Organization>;
  action: CoverageAction;
  /** YYYY-MM-DD, operator-entered: the day it happened. */
  on: string;
  note?: string;
  documents?: Reference<DocumentReference>[];
  /** The latest request on the case, for a payor's answer to point at. */
  request?: Reference<Claim>;
}

/**
 * One coverage step as its record (BAR-3076): what we send is a pre-authorization Claim, the
 * payor's answer a ClaimResponse. Approved keeps the hidden 30-day `preAuthPeriod` the stage
 * expiry reads.
 */
export function buildCoverageActionRecord(input: CoverageActionInput): Claim | ClaimResponse {
  const { patient, benefitCoverage, provider, action, on, note, documents = [], request } = input;
  const insurance = [
    { sequence: 1, focal: true, coverage: { reference: getReferenceString(benefitCoverage.coverage) } },
  ];
  const extension = [
    { url: COVERAGE_ACTION_EXTENSION, valueCoding: { system: COVERAGE_ACTION_CODESYSTEM, code: action } },
    ...documents.map((document) => ({ url: COVERAGE_ACTION_DOCUMENT_EXTENSION, valueReference: document })),
  ];

  if (isCoverageRequest(action)) {
    return {
      resourceType: 'Claim',
      status: 'active',
      use: 'preauthorization',
      type: CLAIM_TYPE_PROFESSIONAL,
      priority: PRIORITY_NORMAL,
      patient,
      created: on,
      provider,
      insurer: benefitCoverage.payor,
      insurance,
      ...(note ? { supportingInfo: [{ sequence: 1, category: INFO_CATEGORY, valueString: note }] } : {}),
      extension,
    };
  }

  return {
    resourceType: 'ClaimResponse',
    status: 'active',
    use: 'preauthorization',
    type: CLAIM_TYPE_PROFESSIONAL,
    patient,
    created: on,
    insurer: benefitCoverage.payor,
    outcome: 'complete',
    insurance,
    ...(request ? { request } : {}),
    ...(action === CoverageAction.APPROVED
      ? { preAuthPeriod: { start: on, end: addDays(on, BENEFIT_APPROVAL_DEFAULT_VALID_DAYS) } }
      : {}),
    ...(note ? { processNote: [{ text: note }] } : {}),
    extension,
  };
}

/** The latest request on the patient's active Coverage, for an answer to point at. */
export async function findLatestCoverageRequest(
  medplum: MedplumClient,
  patientId: string,
  coverage: Coverage
): Promise<Reference<Claim> | undefined> {
  const coverageReference = getReferenceString(coverage);
  const claims = await medplum.searchResources('Claim', { patient: `Patient/${patientId}`, use: 'preauthorization' });
  const latest = [...claims]
    .sort(byRecordedDesc)
    .find((claim) => claim.insurance?.some((insurance) => insurance.coverage?.reference === coverageReference));
  return latest ? { reference: getReferenceString(latest) } : undefined;
}

/** YYYY-MM-DD plus N calendar days, in UTC so no timezone shifts the date. */
export function addDays(isoDate: string, days: number): string {
  const date = new Date(`${isoDate}T00:00:00.000Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

export interface BenefitDecisionSummary {
  decision: BenefitDecision;
  decidedOn: string;
  validThrough?: string;
  /** Approved and today is after valid-through. Computed on read, never stored. */
  expired: boolean;
  denialReason?: BenefitDenialReason;
  recordedAt?: string;
  recordedBy?: Reference;
  claimResponse: ClaimResponse;
}

const DENIAL_REASONS = new Set<string>(Object.values(BenefitDenialReason));

/**
 * Where a recorded step leaves the case, on either track (BAR-3085). The questionnaire, psych
 * evaluation and (retired) PCP clearance only tick the prior auth checklist, so they leave it
 * where it was.
 */
export function coverageCaseState(action: CoverageAction): BenefitCaseState | undefined {
  switch (action) {
    case CoverageAction.APPROVED:
      return BenefitDecision.APPROVED;
    case CoverageAction.APPROVAL_DENIED:
    case CoverageAction.PRIOR_AUTH_DENIED:
    case CoverageAction.NO_BENEFIT:
      return BenefitDecision.DENIED;
    case CoverageAction.QUESTIONNAIRE_RECEIVED:
    case CoverageAction.PSYCH_EVALUATION_RECEIVED:
    case CoverageAction.PCP_CLEARANCE_RECEIVED:
      return undefined;
    default:
      return 'submitted';
  }
}

export function summarizeBenefitDecision(
  claimResponse: ClaimResponse,
  today: string
): BenefitDecisionSummary | undefined {
  const action = readCoverageAction(claimResponse);
  const state = action ? coverageCaseState(action) : undefined;
  if (!state || state === 'submitted' || !claimResponse.created) {
    return undefined;
  }
  const decision = state;
  const reasonCode = claimResponse.extension?.find((e) => e.url === BENEFIT_DENIAL_REASON_EXTENSION)?.valueCoding?.code;
  const decidedOn = claimResponse.created.slice(0, 10);
  const validThrough = claimResponse.preAuthPeriod?.end?.slice(0, 10);
  return {
    decision,
    decidedOn,
    validThrough,
    expired: decision === BenefitDecision.APPROVED && !!validThrough && validThrough < today,
    denialReason: reasonCode && DENIAL_REASONS.has(reasonCode) ? (reasonCode as BenefitDenialReason) : undefined,
    recordedAt: claimResponse.meta?.lastUpdated,
    recordedBy: claimResponse.meta?.author,
    claimResponse,
  };
}

function byRecordedDesc<T extends Claim | ClaimResponse>(a: T, b: T): number {
  return (b.meta?.lastUpdated ?? '').localeCompare(a.meta?.lastUpdated ?? '');
}

/**
 * YYYY-MM-DD for the calendar day `date` falls on in the runtime's timezone. Operator dates are
 * local days: at 6pm in California it is still the 10th, whatever UTC says.
 */
export function localIsoDate(date: Date = new Date()): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/** The current decision on the patient's active episode: the most recently recorded one. */
export async function getCurrentBenefitDecision(
  medplum: MedplumClient,
  patientId: string,
  today: string = localIsoDate()
): Promise<BenefitDecisionSummary | undefined> {
  const coverage = await resolveActiveCoverage(medplum, patientId);
  if (!coverage) {
    return undefined;
  }
  const coverageReference = getReferenceString(coverage);
  const responses = await medplum.searchResources('ClaimResponse', {
    patient: `Patient/${patientId}`,
    use: 'preauthorization',
    _sort: '-_lastUpdated',
  });
  // Sorted again here: the server sort is a hint we do not rely on for correctness.
  for (const response of [...responses].sort(byRecordedDesc)) {
    if (!response.insurance?.some((insurance) => insurance.coverage?.reference === coverageReference)) {
      continue;
    }
    const summary = summarizeBenefitDecision(response, today);
    if (summary) {
      return summary;
    }
  }
  return undefined;
}

/** Where the employer case stands, for the stage (BAR-2595 / BAR-2826). */
export type BenefitCaseState = 'submitted' | BenefitDecision;

/**
 * Where the case on the active Coverage stands, employer or insurance: the latest recorded step
 * that moves it (BAR-3085). Records from before the action codes read as they always did: a
 * Submitted Claim as submitted, a decision as itself. No expiry: Valid through is being dropped
 * (design-system #53).
 */
export async function getBenefitCaseState(
  medplum: MedplumClient,
  patientId: string
): Promise<BenefitCaseState | undefined> {
  const coverage = await resolveActiveCoverage(medplum, patientId);
  if (!coverage) {
    return undefined;
  }
  const coverageReference = getReferenceString(coverage);
  const resources = await searchBenefitEventResources(medplum, patientId);
  for (const resource of resources) {
    if (!resource.insurance?.some((insurance) => insurance.coverage?.reference === coverageReference)) {
      continue;
    }
    const action = readCoverageAction(resource);
    const state = action ? coverageCaseState(action) : undefined;
    if (state) {
      return state;
    }
  }
  return undefined;
}

/**
 * Every Submitted Claim and decision ClaimResponse on the patient, newest recorded first — the
 * timeline's feed. `options` passes through to both searches; Orbit sends `{ cache: 'reload' }`
 * so a refresh is not served from the client's GET cache.
 */
export async function searchBenefitEventResources(
  medplum: MedplumClient,
  patientId: string,
  options?: MedplumRequestOptions
): Promise<(Claim | ClaimResponse)[]> {
  const [claims, responses] = await Promise.all([
    medplum.searchResources('Claim', { patient: `Patient/${patientId}`, use: 'preauthorization' }, options),
    medplum.searchResources('ClaimResponse', { patient: `Patient/${patientId}`, use: 'preauthorization' }, options),
  ]);
  // Responses first, so a decision recorded in the same instant as a Claim wins the stable sort.
  return [...responses, ...claims].sort(byRecordedDesc);
}
